import { useEffect, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { Haptics, ImpactStyle } from '@capacitor/haptics';

const PULL_THRESHOLD = 80;   // px of pull needed to arm the refresh
const HOLD_DURATION = 300;   // ms you must hold past threshold to confirm
const MAX_PULL = 120;         // px — clamp the indicator travel
const INDICATOR_SIZE = 36;    // px — spinner circle diameter

/** Set on body by AriaChatMobile while the main chat view is active (drawer closed). */
export const ARIA_CHAT_PTR_DISABLED_CLASS = 'aria-chat-ptr-disabled';

const isPullToRefreshDisabled = () =>
    document.body.classList.contains(ARIA_CHAT_PTR_DISABLED_CLASS);

/**
 * Adds a native-feeling pull-to-refresh gesture to the app root.
 * Only activates on iOS native (Capacitor). No-op on Android and web.
 *
 * The indicator is injected directly into the DOM so it sits outside
 * the React tree and is never affected by re-renders or route changes.
 */
export function usePullToRefresh() {
    const startYRef = useRef<number | null>(null);
    const pullDistRef = useRef<number>(0);
    const indicatorRef = useRef<HTMLDivElement | null>(null);
    const triggeredRef = useRef(false);
    const holdTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const armedRef = useRef(false); // true once hold timer fires

    useEffect(() => {
        if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'ios') return;

        // --- Build the indicator element ---
        const indicator = document.createElement('div');
        indicator.style.cssText = `
            position: fixed;
            top: 0;
            left: 50%;
            transform: translateX(-50%) translateY(-${INDICATOR_SIZE + 16}px);
            width: ${INDICATOR_SIZE}px;
            height: ${INDICATOR_SIZE}px;
            border-radius: 50%;
            background: var(--white);
            box-shadow: 0 2px 12px rgba(0,0,0,0.18);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 99999;
            transition: none;
            pointer-events: none;
        `;

        const spinner = document.createElement('div');
        spinner.style.cssText = `
            width: 18px;
            height: 18px;
            border: 2px solid var(--g200);
            border-top-color: var(--p500);
            border-radius: 50%;
            animation: ptr-spin 0.7s linear infinite;
            opacity: 0;
        `;

        // Inject keyframes once
        if (!document.getElementById('ptr-keyframes')) {
            const style = document.createElement('style');
            style.id = 'ptr-keyframes';
            style.textContent = `@keyframes ptr-spin { to { transform: rotate(360deg); } }`;
            document.head.appendChild(style);
        }

        indicator.appendChild(spinner);
        document.body.appendChild(indicator);
        indicatorRef.current = indicator;

        // --- Touch handlers ---
        const cancelHoldTimer = () => {
            if (holdTimerRef.current) {
                clearTimeout(holdTimerRef.current);
                holdTimerRef.current = null;
            }
            armedRef.current = false;
        };

        const onTouchStart = (e: TouchEvent) => {
            if (isPullToRefreshDisabled()) return;

            const scrollTop = document.documentElement.scrollTop || document.body.scrollTop;
            if (scrollTop > 2) return;
            startYRef.current = e.touches[0].clientY;
            pullDistRef.current = 0;
            triggeredRef.current = false;
            armedRef.current = false;
        };

        const onTouchMove = (e: TouchEvent) => {
            if (isPullToRefreshDisabled()) {
                startYRef.current = null;
                cancelHoldTimer();
                return;
            }

            if (startYRef.current === null) return;

            const delta = e.touches[0].clientY - startYRef.current;
            if (delta <= 0) {
                startYRef.current = null;
                cancelHoldTimer();
                return;
            }

            // Clamp with rubber-band feel (square-root easing)
            const clamped = Math.min(Math.sqrt(delta) * (MAX_PULL / Math.sqrt(MAX_PULL * 2)), MAX_PULL);
            pullDistRef.current = clamped;

            const progress = Math.min(clamped / PULL_THRESHOLD, 1);
            const translateY = clamped - (INDICATOR_SIZE + 16);

            indicator.style.transform = `translateX(-50%) translateY(${translateY}px)`;
            spinner.style.opacity = String(progress);

            if (clamped >= PULL_THRESHOLD) {
                // Rotate to "ready" position while waiting for hold confirmation
                spinner.style.transform = `rotate(180deg)`;

                // Start hold timer the first time we cross the threshold
                if (!triggeredRef.current) {
                    triggeredRef.current = true;
                    Haptics.impact({ style: ImpactStyle.Light }).catch(() => {});

                    holdTimerRef.current = setTimeout(() => {
                        // Hold confirmed — arm the refresh and give stronger haptic
                        armedRef.current = true;
                        spinner.style.animation = 'ptr-spin 0.7s linear infinite';
                        Haptics.impact({ style: ImpactStyle.Medium }).catch(() => {});
                    }, HOLD_DURATION);
                }
            } else {
                // Pulled back below threshold — cancel the hold timer
                spinner.style.transform = `rotate(${progress * 180}deg)`;
                if (triggeredRef.current) {
                    triggeredRef.current = false;
                    cancelHoldTimer();
                }
            }
        };

        const onTouchEnd = () => {
            if (isPullToRefreshDisabled()) {
                startYRef.current = null;
                cancelHoldTimer();
                return;
            }

            if (startYRef.current === null) return;
            startYRef.current = null;

            if (armedRef.current) {
                // Hold confirmed — reload
                indicator.style.transition = 'transform 0.2s ease';
                indicator.style.transform = `translateX(-50%) translateY(${PULL_THRESHOLD / 2}px)`;
                setTimeout(() => window.location.reload(), 400);
            } else {
                // Not held long enough — cancel and snap back
                cancelHoldTimer();
                triggeredRef.current = false;
                indicator.style.transition = 'transform 0.25s ease';
                indicator.style.transform = `translateX(-50%) translateY(-${INDICATOR_SIZE + 16}px)`;
                setTimeout(() => {
                    indicator.style.transition = 'none';
                    spinner.style.opacity = '0';
                    spinner.style.transform = '';
                    spinner.style.animation = '';
                }, 260);
            }
        };

        document.addEventListener('touchstart', onTouchStart, { passive: true });
        document.addEventListener('touchmove', onTouchMove, { passive: true });
        document.addEventListener('touchend', onTouchEnd, { passive: true });

        return () => {
            document.removeEventListener('touchstart', onTouchStart);
            document.removeEventListener('touchmove', onTouchMove);
            document.removeEventListener('touchend', onTouchEnd);
            cancelHoldTimer();
            indicator.remove();
        };
    }, []);
}
